import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ChevronRight, Calculator, Gauge, ArrowRight, CheckCircle, BookOpen, AlertTriangle } from 'lucide-react';
import SEO, { generateBreadcrumbSchema } from '../components/SEO';

// Expansion coefficients for water (%) per SS-EN 12828, from fill temp ~10°C
const EXPANSION_COEFFICIENTS = [
  { temp: 50, e: 1.18 },
  { temp: 60, e: 1.71 },
  { temp: 70, e: 2.27 },
  { temp: 80, e: 2.89 },
  { temp: 90, e: 3.55 },
  { temp: 100, e: 4.34 },
  { temp: 110, e: 5.15 },
];

const STANDARD_VESSEL_SIZES = [8, 12, 18, 25, 35, 50, 80, 100, 140, 200, 250, 300, 400, 500, 600, 800, 1000];

const webApplicationSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  '@id': 'https://flowingo.se/dimensionering/expansionskarl',
  name: 'Expansionskärl-kalkylator',
  url: 'https://flowingo.se/dimensionering/expansionskarl',
  description: 'Beräkna nominell volym, förtryck och sluttryck för slutna expansionskärl i värmesystem enligt SS-EN 12828.',
  applicationCategory: 'EngineeringApplication',
  operatingSystem: 'Web',
  inLanguage: 'sv-SE',
  isAccessibleForFree: true,
  provider: { '@id': 'https://flowingo.se/#organization' },
};

const interpolateCoefficient = (temp) => {
  const t = Math.min(110, Math.max(50, temp));
  for (let i = 0; i < EXPANSION_COEFFICIENTS.length - 1; i++) {
    const a = EXPANSION_COEFFICIENTS[i];
    const b = EXPANSION_COEFFICIENTS[i + 1];
    if (t >= a.temp && t <= b.temp) {
      return a.e + ((t - a.temp) / (b.temp - a.temp)) * (b.e - a.e);
    }
  }
  return EXPANSION_COEFFICIENTS[EXPANSION_COEFFICIENTS.length - 1].e;
};

const ExpansionCalculatorPage = () => {
  const { language } = useLanguage();
  const [step, setStep] = useState(1);
  const [systemVolume, setSystemVolume] = useState('');
  const [maxTemp, setMaxTemp] = useState('');
  const [staticHeight, setStaticHeight] = useState('');
  const [safetyValve, setSafetyValve] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const calculate = () => {
    setError('');
    const Vs = parseFloat(systemVolume);
    const tMax = parseFloat(maxTemp);
    const Hst = parseFloat(staticHeight);
    const psv = parseFloat(safetyValve);

    if (!Vs || !tMax || isNaN(Hst) || !psv) {
      setError('Fyll i alla fält med giltiga värden.');
      return;
    }

    const e = interpolateCoefficient(tMax);
    const Ve = Vs * (e / 100);
    const Vv = Math.max(0.005 * Vs, 3);
    const p0 = Math.max(Hst / 10 + 0.3, 0.5);
    const pe = psv <= 5 ? psv - 0.5 : psv * 0.9;

    if (pe <= p0) {
      setError(`Sluttrycket (${pe.toFixed(1)} bar) måste vara högre än förtrycket (${p0.toFixed(1)} bar). Öka säkerhetsventilens öppningstryck eller minska den statiska höjden.`);
      return;
    }

    const Vn = (Ve + Vv) * ((pe + 1) / (pe - p0));
    const recommendedSize = STANDARD_VESSEL_SIZES.find((s) => s >= Vn) || STANDARD_VESSEL_SIZES[STANDARD_VESSEL_SIZES.length - 1];

    setResult({
      e: e.toFixed(2),
      Ve: Ve.toFixed(1),
      Vv: Vv.toFixed(1),
      p0: p0.toFixed(2),
      pe: pe.toFixed(2),
      Vn: Math.ceil(Vn),
      recommendedSize,
      oversized: Vn > STANDARD_VESSEL_SIZES[STANDARD_VESSEL_SIZES.length - 1],
    });
    setStep(3);
  };

  const reset = () => {
    setStep(1);
    setSystemVolume('');
    setMaxTemp('');
    setStaticHeight('');
    setSafetyValve('');
    setResult(null);
    setError('');
  };

  return (
    <div className="bg-white min-h-screen pt-24">
      <SEO
        title="Dimensionera expansionskärl enligt SS-EN 12828"
        description="Kostnadsfri kalkylator för slutna expansionskärl: beräkna nominell volym, förtryck och sluttryck enligt SS-EN 12828. Med expansionstabell och komplett räkneexempel."
        language={language}
        schema={[
          generateBreadcrumbSchema([
            { name: 'Hem', url: 'https://flowingo.se' },
            { name: 'Dimensionering', url: 'https://flowingo.se/dimensionering' },
            { name: 'Expansionskärl', url: 'https://flowingo.se/dimensionering/expansionskarl' },
          ]),
          webApplicationSchema,
        ]}
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-stone-500 mb-8">
          <Link to="/" className="hover:text-stone-900">Hem</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/dimensionering" className="hover:text-stone-900">Dimensionering</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-stone-900">Expansionskärl</span>
        </nav>

        {/* Hero */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-stone-100 px-4 py-2 rounded-full mb-6">
            <Gauge className="w-4 h-4 text-[#321a27]" />
            <span className="text-sm font-medium text-stone-700">Enligt SS-EN 12828</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-stone-900 mb-6">
            Dimensionera expansionskärl
          </h1>
          <p className="text-xl text-stone-600 max-w-2xl mx-auto">
            Beräkna nominell kärlvolym, förtryck och sluttryck för slutna expansionskärl i värmesystem
            – enligt SS-EN 12828.
          </p>
        </div>

        {/* Progress */}
        <div className="mb-12">
          <div className="flex justify-between mb-2">
            {[1, 2, 3].map((s) => (
              <div key={s} className={`flex-1 h-1 ${s <= step ? 'bg-[#321a27]' : 'bg-stone-200'} ${s < 3 ? 'mr-2' : ''}`} />
            ))}
          </div>
          <div className="flex justify-between text-xs text-stone-500">
            <span className={step >= 1 ? 'text-[#321a27] font-medium' : ''}>Systemdata</span>
            <span className={step >= 2 ? 'text-[#321a27] font-medium' : ''}>Tryck</span>
            <span className={step >= 3 ? 'text-[#321a27] font-medium' : ''}>Resultat</span>
          </div>
        </div>

        {/* Step 1: System data */}
        {step === 1 && (
          <div className="max-w-xl mx-auto bg-stone-50 border border-stone-200 p-8" data-testid="expansion-step-1">
            <h2 className="text-2xl font-semibold text-stone-900 mb-6">Systemdata</h2>
            <div className="space-y-6">
              <div>
                <Label htmlFor="systemVolume" className="text-stone-700">Systemvolym (liter)</Label>
                <Input
                  id="systemVolume"
                  data-testid="expansion-system-volume-input"
                  type="number"
                  placeholder="t.ex. 1000"
                  value={systemVolume}
                  onChange={(e) => setSystemVolume(e.target.value)}
                  className="mt-2 bg-white border-stone-300 rounded-none"
                />
                <p className="text-xs text-stone-500 mt-2">
                  Okänd volym? Uppskatta: radiatorsystem ≈ 11–14 l/kW, golvvärme ≈ 17–20 l/kW,
                  fläktkonvektorer ≈ 7–9 l/kW panneffekt.
                </p>
              </div>
              <div>
                <Label htmlFor="maxTemp" className="text-stone-700">Maximal framledningstemperatur (°C)</Label>
                <Input
                  id="maxTemp"
                  data-testid="expansion-max-temp-input"
                  type="number"
                  placeholder="t.ex. 90"
                  value={maxTemp}
                  onChange={(e) => setMaxTemp(e.target.value)}
                  className="mt-2 bg-white border-stone-300 rounded-none"
                />
                <p className="text-xs text-stone-500 mt-2">50–110 °C. Vattnets expansionskoefficient interpoleras från tabellen nedan.</p>
              </div>
              <Button
                data-testid="expansion-step-1-next"
                onClick={() => systemVolume && maxTemp && setStep(2)}
                disabled={!systemVolume || !maxTemp}
                className="w-full bg-[#321a27] hover:bg-[#4a2d3d] text-white rounded-none py-6"
              >
                Nästa: Tryckuppgifter <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Pressures */}
        {step === 2 && (
          <div className="max-w-xl mx-auto bg-stone-50 border border-stone-200 p-8" data-testid="expansion-step-2">
            <h2 className="text-2xl font-semibold text-stone-900 mb-6">Tryckuppgifter</h2>
            <div className="space-y-6">
              <div>
                <Label htmlFor="staticHeight" className="text-stone-700">Statisk höjd (meter)</Label>
                <Input
                  id="staticHeight"
                  data-testid="expansion-static-height-input"
                  type="number"
                  placeholder="t.ex. 15"
                  value={staticHeight}
                  onChange={(e) => setStaticHeight(e.target.value)}
                  className="mt-2 bg-white border-stone-300 rounded-none"
                />
                <p className="text-xs text-stone-500 mt-2">
                  Höjdskillnaden från expansionskärlets anslutning till systemets högsta punkt.
                  Förtryck p₀ = statisk höjd/10 + 0,3 bar.
                </p>
              </div>
              <div>
                <Label htmlFor="safetyValve" className="text-stone-700">Säkerhetsventilens öppningstryck (bar)</Label>
                <Input
                  id="safetyValve"
                  data-testid="expansion-safety-valve-input"
                  type="number"
                  placeholder="t.ex. 3"
                  value={safetyValve}
                  onChange={(e) => setSafetyValve(e.target.value)}
                  className="mt-2 bg-white border-stone-300 rounded-none"
                />
                <p className="text-xs text-stone-500 mt-2">
                  Sluttryck p<sub>e</sub> = öppningstryck − 0,5 bar (≤ 5 bar) eller 90 % av öppningstrycket (&gt; 5 bar).
                </p>
              </div>
              {error && (
                <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 p-3" data-testid="expansion-error">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  {error}
                </div>
              )}
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1 border-stone-300 rounded-none py-6">
                  Tillbaka
                </Button>
                <Button
                  data-testid="expansion-calculate-button"
                  onClick={calculate}
                  disabled={!staticHeight || !safetyValve}
                  className="flex-1 bg-[#321a27] hover:bg-[#4a2d3d] text-white rounded-none py-6"
                >
                  <Calculator className="w-4 h-4 mr-2" /> Beräkna
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Result */}
        {step === 3 && result && (
          <div className="max-w-2xl mx-auto" data-testid="expansion-result">
            <div className="bg-[#321a27] text-white p-8 text-center mb-6">
              <CheckCircle className="w-10 h-10 mx-auto mb-4 opacity-90" />
              <p className="uppercase tracking-wider text-sm opacity-80 mb-2">Rekommenderad nominell volym</p>
              <p className="text-5xl font-bold mb-2" data-testid="expansion-recommended-size">{result.recommendedSize} liter</p>
              <p className="opacity-80 text-sm">Beräknad minsta volym: {result.Vn} liter</p>
            </div>

            <div className="bg-stone-50 border border-stone-200 p-6 mb-6">
              <h2 className="font-semibold text-stone-900 mb-4">Beräkningsdetaljer (SS-EN 12828)</h2>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <dt className="text-stone-500">Expansionskoefficient e</dt><dd className="text-right font-medium">{result.e} %</dd>
                <dt className="text-stone-500">Expansionsvolym V<sub>e</sub></dt><dd className="text-right font-medium">{result.Ve} l</dd>
                <dt className="text-stone-500">Vattenreserv V<sub>v</sub> (min 0,5 % / 3 l)</dt><dd className="text-right font-medium">{result.Vv} l</dd>
                <dt className="text-stone-500">Förtryck p₀</dt><dd className="text-right font-medium">{result.p0} bar</dd>
                <dt className="text-stone-500">Sluttryck p<sub>e</sub></dt><dd className="text-right font-medium">{result.pe} bar</dd>
              </dl>
              {result.oversized && (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 p-3 mt-4">
                  Beräknad volym överstiger största standardkärl – överväg flera kärl i parallell eller ett kompressorstyrt tryckhållningssystem. Kontakta oss för dimensioneringshjälp.
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <Link to="/produkter/kategori/expansionssystem">
                <Button data-testid="expansion-view-products" className="w-full bg-[#321a27] hover:bg-[#4a2d3d] text-white rounded-none py-6">
                  Se våra expansionssystem <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/snabboffert">
                <Button variant="outline" className="w-full border-[#321a27] text-[#321a27] rounded-none py-6">
                  Begär offert
                </Button>
              </Link>
            </div>
            <button onClick={reset} data-testid="expansion-reset" className="text-sm text-stone-500 hover:text-stone-900 underline mx-auto block">
              Gör en ny beräkning
            </button>
          </div>
        )}

        {/* Static reference content (extractable for search/AI) */}
        <div className="mt-20 max-w-3xl mx-auto space-y-12">
          <section>
            <h2 className="text-2xl font-semibold text-stone-900 mb-4">Hur beräknar man expansionskärlets volym?</h2>
            <p className="text-stone-600 leading-relaxed mb-4">
              Enligt SS-EN 12828 dimensioneras ett slutet expansionskärl i fyra steg. Först bestäms systemets
              expansionsvolym V<sub>e</sub> = systemvolym × expansionskoefficient, där koefficienten beror på maximal
              framledningstemperatur (se tabellen nedan). Därefter adderas en vattenreserv V<sub>v</sub> på minst 0,5 %
              av systemvolymen, dock lägst 3 liter. Förtrycket p₀ sätts till den statiska höjden delad med 10 plus
              0,3 bar, och sluttrycket p<sub>e</sub> till säkerhetsventilens öppningstryck minus 0,5 bar (vid öppningstryck
              upp till 5 bar). Nominell kärlvolym beräknas slutligen som V<sub>n</sub> = (V<sub>e</sub> + V<sub>v</sub>) × (p<sub>e</sub> + 1) / (p<sub>e</sub> − p₀),
              och avrundas uppåt till närmaste standardstorlek.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-stone-900 mb-4">Vattnets expansionskoefficient vid olika temperaturer</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-stone-200" data-testid="expansion-coefficient-table">
                <thead>
                  <tr className="bg-stone-100 text-left">
                    <th className="p-3 border-b border-stone-200 font-semibold">Max framledningstemperatur</th>
                    <th className="p-3 border-b border-stone-200 font-semibold text-right">Expansionskoefficient e</th>
                  </tr>
                </thead>
                <tbody>
                  {EXPANSION_COEFFICIENTS.map((row) => (
                    <tr key={row.temp} className="odd:bg-white even:bg-stone-50">
                      <td className="p-3 border-b border-stone-100">{row.temp} °C</td>
                      <td className="p-3 border-b border-stone-100 text-right">{row.e.toFixed(2)} %</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-stone-500 mt-2">Gäller rent vatten med fyllnadstemperatur ca 10 °C. Glykolblandningar expanderar mer – räkna med ca 1,1–1,3 × koefficienten beroende på inblandning.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-stone-900 mb-4">Räkneexempel: villa- och fastighetssystem</h2>
            <p className="text-stone-600 leading-relaxed">
              Ett värmesystem med 1 000 liter systemvolym, 90 °C maximal framledningstemperatur, 15 meters statisk
              höjd och säkerhetsventil på 3 bar dimensioneras så här: expansionskoefficienten vid 90 °C är 3,55 %,
              vilket ger expansionsvolymen V<sub>e</sub> = 1 000 × 0,0355 = 35,5 liter. Vattenreserven blir 5 liter
              (0,5 % av 1 000 l). Förtrycket p₀ = 15/10 + 0,3 = 1,8 bar och sluttrycket p<sub>e</sub> = 3 − 0,5 = 2,5 bar.
              Nominell volym: V<sub>n</sub> = (35,5 + 5) × (2,5 + 1) / (2,5 − 1,8) = 40,5 × 5 = 202,5 liter.
              Närmaste standardstorlek uppåt är ett 250-literskärl.
            </p>
          </section>

          <section className="bg-stone-50 border border-stone-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-5 h-5 text-[#321a27]" />
              <h2 className="text-lg font-semibold text-stone-900">Fördjupning i Kunskapshubben</h2>
            </div>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/kunskap/dimensionering-av-slutna-expansionskarl" className="text-[#321a27] underline hover:no-underline" data-testid="expansion-guide-link-1">
                  Dimensionering av slutna expansionskärl – komplett guide
                </Link>
              </li>
              <li>
                <Link to="/kunskap/sa-dimensionerar-du-expansionskarl-steg-for-steg" className="text-[#321a27] underline hover:no-underline" data-testid="expansion-guide-link-2">
                  Så dimensionerar du expansionskärl – steg för steg
                </Link>
              </li>
              <li>
                <Link to="/produkter/kategori/expansionssystem" className="text-[#321a27] underline hover:no-underline">
                  Expansionssystem i vårt sortiment
                </Link>
              </li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ExpansionCalculatorPage;
